import { Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { DigitalLibraryService } from 'src/app/digital-library/services/digital-library.service';
import { PagesService } from 'src/app/static-pages/service/pages.service';
import { environment } from 'src/environments/environment';
import { UserPermissions } from '../enums/permissions.enum';
import { UserPermission } from '../enums/user-permission.enum';
import { FilterModel } from '../models/filter.model';
import { ApplicationRoutes } from '../routes/application-routes.const';
import { ApiClientService } from '../services/api-client.service';
import { PortalLinkContext, PortalPageSummary } from './portal-links';

/**
 * Loads the data the navigation builders in `portal-links.ts` need (signed-in user, roles, digital library
 * availability, active CMS pages) once per session and shares it between the header, footer and sitemap.
 */
@Injectable({ providedIn: 'root' })
export class PortalNavigationService {
  /** Active pages from `GET /settings/pages/active`; falls back to `GET /settings/pages` until the endpoint is deployed. */
  readonly pages$: Observable<PortalPageSummary[]> = this.apiClient.get(ApplicationRoutes.Page.Active).pipe(
    catchError(() => this.pagesService.getPages()),
    catchError(() => of([])),
    map((result: unknown) => (Array.isArray(result) ? (result as PortalPageSummary[]) : [])),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  readonly showDigitalLibrary$: Observable<boolean> = (
    this.digitalLibraryService.getPaging(new FilterModel(0, 5)) as Observable<{ total?: number }>
  ).pipe(
    map((result) => !!result && result.total !== 0),
    catchError(() => of(false)),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  readonly context$: Observable<PortalLinkContext> = combineLatest([
    this.authService.currentUser,
    this.permissionsService.permissions$,
    this.showDigitalLibrary$,
    this.pages$,
  ]).pipe(
    map(([user, permissions, showDigitalLibrary, pages]) => ({
      isSignedIn: !!(user as { email?: string })?.email,
      isStudent: !!permissions?.[UserPermission.Student],
      canViewDashboard: !!permissions?.[UserPermissions.Settings.DashboardView],
      dashboardUrl: environment.dashboardUrl,
      showDigitalLibrary,
      userId: (user as { id?: number })?.id ?? null,
      pages,
    })),
  );

  constructor(
    private apiClient: ApiClientService,
    private pagesService: PagesService,
    private digitalLibraryService: DigitalLibraryService,
    private authService: AuthService,
    private permissionsService: NgxPermissionsService,
  ) {}
}
